import { getAllUsers, deleteRider, updateUserRole, updateRider, uploadImage } from '../../Utils/apiServices';
import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    TextField,
    IconButton,
    Snackbar,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    InputAdornment,
    Chip,
    Typography,
    CircularProgress,
    Backdrop,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmDialog from '../../Components/ConfirmDialog';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editUser, setEditUser] = useState({
        id: '',
        name: '',
        user_id: '',
        dob: null,
        user_type: '',
        email: '',
        profile_picture: ''
    });
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const fetchUsers = async () => {
        setPageLoading(true);
        try {
            const res = await getAllUsers();
            setUsers(res.data.data);
            setFilteredUsers(res.data.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            showSnackbar('Error fetching users', 'error');
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const openDeleteUserConfirm = (userId) => {
        setDeleteUserId(userId);
        setDeleteDialogOpen(true);
    };

    const closeDeleteUserConfirm = () => {
        if (loading) return;
        setDeleteDialogOpen(false);
        setDeleteUserId(null);
    };

    const confirmDeleteUser = async () => {
        if (deleteUserId == null) return;
        setLoading(true);
        try {
            await deleteRider(deleteUserId);
            showSnackbar('User deleted successfully');
            setDeleteDialogOpen(false);
            setDeleteUserId(null);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            showSnackbar('Error deleting user', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        
        const filtered = users.filter(user => 
            (user.name?.toLowerCase() || '').includes(term) ||
            (user.user_id?.toLowerCase() || '').includes(term) ||
            (user.email?.toLowerCase() || '').includes(term) ||
            (user.user_type?.toLowerCase() || '').includes(term)
        );
        
        setFilteredUsers(filtered);
    };

    const handleConvertToRider = async (userId, userIdNumber) => {
        setLoading(true);
        try {
            await updateUserRole({
                id: userId,
                user_id: userIdNumber
            });
            showSnackbar('User converted to Rider successfully');
            fetchUsers();
        } catch (error) {
            console.error('Error converting user to rider:', error);
            showSnackbar('Error converting user to rider', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setIsEditing(true);
        setEditUser({
            id: user.id,
            name: user.name,
            user_id: user.user_id,
            dob: user.dob ? new Date(user.dob) : null,
            user_type: user.user_type,
            email: user.email,
            profile_picture: user.profile_picture
        });
        handleOpen();
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setIsEditing(false);
        setEditUser({
            id: '',
            name: '',
            user_id: '',
            dob: null,
            user_type: '',
            email: '',
            profile_picture: ''
        });
    };

    const handleChange = (e) => {
        setEditUser({
            ...editUser,
            [e.target.name]: e.target.value
        });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await uploadImage(formData);
            setEditUser({
                ...editUser,
                profile_picture: response.data.file[0]
            });
        } catch (error) {
            console.error('Error uploading image:', error);
            setSnackbar({
                open: true,
                message: 'Error uploading image',
                severity: 'error'
            });
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const userData = {
                id: editUser.id,
                name: editUser.name,
                user_id: editUser.user_id,
                dob: editUser.dob,
                user_type: editUser.user_type,
                email: editUser.email,
                profile_picture: editUser.profile_picture
            };

            await updateRider(userData);
            showSnackbar('User updated successfully');
            handleClose();
            fetchUsers();
        } catch (error) {
            console.error('Error updating user:', error);
            showSnackbar('Error updating user', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Colorful page banner */}
            <Box
                sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 8px 24px rgba(139,92,246,0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -40,
                        right: -40,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                    },
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>👥 Users Management</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>Manage admin users and convert users to riders</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{users.length}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Total Users</Typography>
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <TextField
                    placeholder="Search by name, user ID, email, or type..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ maxWidth: 420 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Showing {filteredUsers.length} of {users.length} users
                </Typography>
            </Paper>

            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete user"
                message="Are you sure you want to delete this user? This action cannot be undone."
                confirmLabel="Delete"
                onCancel={closeDeleteUserConfirm}
                onConfirm={confirmDeleteUser}
                loading={loading}
            />

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{isEditing ? 'Edit User' : 'User Details'}</DialogTitle>
                <DialogContent>
                    <TextField
                        name="user_id"
                        label="User ID"
                        fullWidth
                        margin="dense"
                        value={editUser.user_id}
                        onChange={handleChange}
                    />
                    <TextField
                        name="dob"
                        label="Date of Birth"
                        type="date"
                        fullWidth
                        margin="dense"
                        value={editUser.dob ? new Date(editUser.dob).toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                            setEditUser({
                                ...editUser,
                                dob: new Date(e.target.value)
                            });
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        name="name"
                        label="Name"
                        fullWidth
                        margin="dense"
                        value={editUser.name}
                        onChange={handleChange}
                    />
                    <TextField
                        name="email"
                        label="Email"
                        fullWidth
                        margin="dense"
                        value={editUser.email}
                        onChange={handleChange}
                    />
                    <TextField
                        name="profile_picture"
                        label="Profile Picture"
                        fullWidth
                        margin="dense"
                        type="file"
                        onChange={handleImageChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleClose} 
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Updating...' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {pageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    '& .MuiTableCell-head': {
                                        bgcolor: 'grey.50',
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        fontSize: '0.8125rem',
                                        letterSpacing: '0.02em',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        py: 1.5,
                                    },
                                }}
                            >
                                <TableCell>ID</TableCell>
                                <TableCell>User ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>User Type</TableCell>
                                <TableCell>Date of Birth</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((user) => (
                                <TableRow
                                    key={user.id}
                                    hover
                                    sx={{ '&:last-child td': { borderBottom: 0 }, transition: 'background-color 0.15s' }}
                                >
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{user.id}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{user.user_id}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{user.name}</TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                                    <TableCell>
                                        <Chip label={user.user_type || '—'} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                                        {user.dob ? new Date(user.dob).toLocaleDateString() : '—'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <IconButton
                                                onClick={() => handleEdit(user)}
                                                color="primary"
                                                size="small"
                                                disabled={loading}
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                                aria-label="Edit user"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            {user.user_type !== 'Rider' && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                    startIcon={loading ? <CircularProgress size={14} /> : <PersonAddIcon fontSize="small" />}
                                                    onClick={() => handleConvertToRider(user.id, user.user_id)}
                                                    disabled={loading}
                                                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                                >
                                                    {loading ? 'Converting…' : 'To Rider'}
                                                </Button>
                                            )}
                                            <IconButton
                                                onClick={() => openDeleteUserConfirm(user.id)}
                                                color="error"
                                                size="small"
                                                disabled={loading}
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                                aria-label="Delete user"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    elevation={6}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default Users;